import { AgentStep, MessageWithCitations } from './chatTypes';

export const OPEN_INSPECTOR_EVENT = 'powerai:open-inspector';

export function openInspector(): void {
  window.dispatchEvent(new Event(OPEN_INSPECTOR_EVENT));
}

export function localizeStepLabel(label: string): string {
  const map: Record<string, string> = {
    'Searching the web': 'Mencari di web',
    'Reading search results': 'Membaca hasil pencarian',
    'Reading file': 'Membaca file',
    'Viewing image': 'Melihat gambar',
    'Writing answer': 'Menyusun jawaban',
    'Generating image': 'Membuat gambar',
    'Building presentation': 'Menyusun presentasi',
    'Drawing comic': 'Menggambar komik',
    'Thinking': 'Berpikir',
    'Saving memory': 'Menyimpan memori',
  };
  if (map[label]) return map[label];
  const searching = label.match(/^Searching (.+)$/);
  if (searching) return `Mencari di ${searching[1]}`;
  return label;
}

export function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '');
  }
}

export function faviconUrl(host: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`;
}

export type InspectorSource = {
  kind: 'web' | 'file' | 'memory';
  title: string;
  meta: string;
  href?: string;
};

export function groupInspectorSources(sources: InspectorSource[]): {
  web: InspectorSource[];
  file: InspectorSource[];
  memory: InspectorSource[];
} {
  return {
    web: sources.filter((source) => source.kind === 'web'),
    file: sources.filter((source) => source.kind === 'file'),
    memory: sources.filter((source) => source.kind === 'memory'),
  };
}

export type InspectorState = {
  steps: AgentStep[];
  streaming: boolean;
  durationMs: number | null;
  sources: InspectorSource[];
};

const EMPTY_INSPECTOR: InspectorState = {
  steps: [],
  streaming: false,
  durationMs: null,
  sources: [],
};

export function inspectFromMessages(messages?: MessageWithCitations[]): InspectorState {
  if (!messages || messages.length === 0) return EMPTY_INSPECTOR;

  let target: MessageWithCitations | undefined;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    if (message.isComplete === false) {
      target = message;
      break;
    }
    if ((message.steps && message.steps.length > 0)
      || (message.sources && message.sources.length > 0)
      || (message.memorySaved && message.memorySaved.length > 0)) {
      target = message;
      break;
    }
  }
  if (!target) return EMPTY_INSPECTOR;

  const sources: InspectorSource[] = [];
  const seenWeb = new Set<string>();
  for (const url of target.sources || []) {
    const href = (url || '').trim();
    if (!href || seenWeb.has(href)) continue;
    seenWeb.add(href);
    const host = domainOf(href);
    let path = '';
    try {
      const parsed = new URL(href);
      path = `${parsed.pathname}${parsed.search}`.replace(/\/$/, '');
    } catch {
      path = '';
    }
    sources.push({
      kind: 'web',
      title: host || href,
      meta: path && path !== '/' ? path : 'web',
      href,
    });
  }

  const seenFile = new Set<string>();
  for (const step of target.steps || []) {
    if (step.label !== 'Reading file' || !step.detail) continue;
    const name = step.detail.trim();
    if (!name || seenFile.has(name)) continue;
    seenFile.add(name);
    sources.push({ kind: 'file', title: name, meta: 'file' });
  }

  for (const memory of target.memorySaved || []) {
    const text = memory.trim();
    if (!text) continue;
    sources.push({ kind: 'memory', title: text, meta: 'memori' });
  }

  return {
    steps: target.steps || [],
    streaming: target.isComplete === false,
    durationMs: typeof target.traceDurationMs === 'number' ? target.traceDurationMs : null,
    sources,
  };
}
